import type { CardExport } from '@molt/shared';
import type { ReactElement } from 'react';
import { AvatarStack, LabelChip, MarkdownBody } from '@/components';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { formatLocaleCardDate, formatLocaleCardDueDate } from '@/lib/locale-formatting';
import { useLocalePreferencesValue } from '@/lib/locale-preferences';

export interface CardPrintViewProps {
  readonly exportData: CardExport;
}

/**
 * FE-80.1 — the card print view, reached only through `SharePanel`'s print
 * toggle. Every string this file authored is now read from the catalogue; the
 * card title, the list title, label names, member names, checklist titles and
 * item text, and the attachment names all stay literal, because they are board
 * DATA and a translator must never see them.
 *
 * The dates and member-list separator are locale-owned formatting, not
 * catalogue strings. They use the stored locale preferences while keeping the
 * underlying card/member values literal.
 */
export function CardPrintView({ exportData }: CardPrintViewProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const localePreferences = useLocalePreferencesValue();
  const { card } = exportData;
  const hasDates = card.startAt !== null || card.dueAt !== null;
  const formattedMembers = memberList(
    localePreferences.language,
    exportData.members.map((member) => member.displayName),
  );

  return (
    <article className="flex flex-col gap-4 text-ink">
      <header className="flex flex-col gap-2">
        <p className="text-xs font-medium uppercase text-ink-muted">
          {t('cardBack.print.eyebrow')}
        </p>
        <h3 className="break-words font-display text-2xl text-ink">{card.title}</h3>
        <p className="text-sm text-ink-muted">
          {t('cardBack.print.inList', { list: card.list.title })}
        </p>
      </header>

      <PrintSection title={t('cardBack.print.description')}>
        {card.description === null ? (
          <p className="text-sm text-ink-muted">{t('cardBack.print.noDescription')}</p>
        ) : (
          <MarkdownBody markdown={card.description} variant="description" />
        )}
      </PrintSection>

      <PrintSection title={t('cardBack.print.labels')}>
        {exportData.labels.length === 0 ? (
          <p className="text-sm text-ink-muted">{t('cardBack.print.noLabels')}</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {exportData.labels.map((label) => (
              <LabelChip
                key={label.id}
                colour={label.colour}
                name={label.name}
                size="sm"
              />
            ))}
          </div>
        )}
      </PrintSection>

      <PrintSection title={t('cardBack.print.dates')}>
        {hasDates ? (
          <dl className="grid gap-1 text-sm">
            {card.startAt !== null ? (
              <PrintDate
                label={t('cardBack.print.start')}
                dateTime={card.startAt}
                value={formatLocaleCardDate(t, card.startAt, localePreferences)}
              />
            ) : null}
            {card.dueAt !== null ? (
              <PrintDate
                label={t('cardBack.print.due')}
                dateTime={card.dueAt}
                value={formatLocaleCardDueDate(
                  t,
                  card.dueAt,
                  localePreferences,
                  card.dueAllDay,
                )}
              />
            ) : null}
            <div className="flex gap-2">
              <dt className="font-medium">{t('cardBack.print.completed')}</dt>
              <dd>
                {card.completed
                  ? t('cardBack.print.completedYes')
                  : t('cardBack.print.completedNo')}
              </dd>
            </div>
          </dl>
        ) : (
          <p className="text-sm text-ink-muted">{t('cardBack.print.noDates')}</p>
        )}
      </PrintSection>

      <PrintSection title={t('cardBack.print.members')}>
        {exportData.members.length === 0 ? (
          <p className="text-sm text-ink-muted">{t('cardBack.print.noMembers')}</p>
        ) : (
          <div className="flex items-center gap-2">
            <AvatarStack members={exportData.members} max={6} size="sm" />
            <span className="text-sm">{formattedMembers}</span>
          </div>
        )}
      </PrintSection>

      <PrintSection title={t('cardBack.print.checklists')}>
        {exportData.checklists.length === 0 ? (
          <p className="text-sm text-ink-muted">{t('cardBack.print.noChecklists')}</p>
        ) : (
          <div className="flex flex-col gap-3">
            {exportData.checklists.map((checklist) => (
              <section
                key={checklist.id}
                className="rounded-card border border-border p-3"
              >
                <h5 className="font-medium">{checklist.title}</h5>
                <ul className="mt-2 grid gap-1 text-sm">
                  {checklist.items.map((item) => (
                    <li key={item.id}>
                      {item.checked
                        ? t('cardBack.print.itemChecked')
                        : t('cardBack.print.itemUnchecked')}{' '}
                      {item.text}
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </PrintSection>

      <PrintSection title={t('cardBack.print.attachments')}>
        {exportData.attachments.length === 0 ? (
          <p className="text-sm text-ink-muted">{t('cardBack.print.noAttachments')}</p>
        ) : (
          <ul className="grid gap-1 text-sm">
            {exportData.attachments.map((attachment) => (
              <li key={attachment.id}>{attachment.displayName}</li>
            ))}
          </ul>
        )}
      </PrintSection>
    </article>
  );
}

function PrintSection({
  title,
  children,
}: {
  readonly title: string;
  readonly children: ReactElement;
}): ReactElement {
  return (
    <section className="flex flex-col gap-2">
      <h4 className="text-sm font-semibold uppercase text-ink-muted">{title}</h4>
      {children}
    </section>
  );
}

function PrintDate({
  label,
  dateTime,
  value,
}: {
  readonly label: string;
  readonly dateTime: string;
  readonly value: string;
}) {
  return (
    <div className="flex gap-2">
      <dt className="font-medium">{label}</dt>
      <dd>
        <time dateTime={dateTime}>{value}</time>
      </dd>
    </div>
  );
}

function memberList(locale: string, members: readonly string[]): string {
  return new Intl.ListFormat(locale, {
    style: 'long',
    type: 'conjunction',
  }).format(members);
}
